import _ from "lodash";
import {VGridVueTemplate} from "@entestat/vue-datagrid";
import Vue from "vue";
import SessionManager from "@/utils/SessionManager";
import Activity from "@/activity/Activity";
import {SCOPE_DEVICE_SIZE} from "@/utils/ConfigManager";
import MD5 from "crypto-js/md5";
import MetaRenderer from "@/components/datagrid/MetaRenderer";
import RtModule from "@/utils/RtModule";
import Events from "@/utils/Events";
import LoadingCellRenderer from "@/components/datagrid/LoadingCellRenderer.vue";
import FeatureFlag from "@/server/com/silkplm/rpc/FeatureFlag";
import DefaultCellTextRenderer from "@/components/datagrid/DefaultCellTextRenderer.vue";
import * as EntityEditFormService from "@/components/forms/customizations/plm/EntityEditFormService";


function makeRendererFactory(template){
    return function () { return Vue.extend(template); };
}
export class ColumnDefinition {
    constructor() {
        this.name = "";
        this.prop = "";
        this.autoSize = true;
        this.sortable = true;
        this.searchable = true;
        this.size = 99;
        this.visible = true;
        this.template = null;
        this.toStr = null;
        this.toStrFromInstance = null;
        this.toFilterStr = null;
        this.fieldConfig = null;
        this.formConfig = null;
        this.cssClass = "";
        this.cssHStyle = "";

        this.colId = null;
        this.id = null;
        this.editorComponent = null;
    }
}

export class ColumnConfig {
    constructor() {
        this.visible = null;
        this.size = null;
        this.order = -10;
    }
}

export class ColVisibility {
    constructor() {
        this.colId = "";
        this.colLabel = "";
        this.visible = true;
    }
}

let V3Renderers = {};

export class ColumnsManager {
    constructor(dataGrid) {
        this.renderers = {};
        this.v3renderers = {};

        /** @type {ColumnDefinition[]} */
        this.allColumns = [];

        this.orderedColumns = [];

        /** @type {DataGrid2} */
        this.dataGrid = dataGrid;
        this.config = {cols: {}, pinnedCols: 0};
        this.configLoaded = false;

        this.visibleColumns2AllCoumnsMap = [];

        this.metaColumn = {};
        this.multiSelectEnabled = false;
        this.applyDefaultSorting = true;
        this.v3 = this.dataGrid.v3;
    }

    postConstruct() {
        let that = this;
        this.loadConfig();
        this.dataGrid?.getGridHeaderCols()?.addEventListener("headerResize", (e) => that.onHeaderResize(e));
        this.dataGrid?.$refs.baseGrid?.$on("headerResize", (e) => that.onBaseGridHeaderResize(e));
        this.dataGrid?.$refs.baseGrid?.$on("orderChange", (e) => that.onBaseGridOrderChange(e));
    }

    detectConfigId() {
        let isView = false;
        if (this.dataGrid?.$props?.form?.isView) {
            isView = true;
        }
        if (this.dataGrid.name) {
            if (isView) {
                return "VIEW_" + this.dataGrid.name
            }
            return this.dataGrid.name;
        }

        let searchRoot = this.dataGrid.$el;
        let activityEl = null;
        if (!searchRoot) {
            searchRoot = this.dataGrid.$parent;
            if (searchRoot?.isActivity) {
                activityEl = searchRoot;
            }
            while (!searchRoot.$el) {
                if (searchRoot?.isActivity) {
                    activityEl = searchRoot;
                }

                if (!searchRoot.$parent) {
                    return undefined;
                }
                searchRoot = searchRoot.$parent;
            }
        }
        let activityDiv = Activity.findActivityRootDiv(searchRoot.$el);
        let activityClass = Activity.findActivityClass(searchRoot.$el);
        if (!activityClass) {
            if (activityEl) {
                activityClass = activityEl?.$options?.name;
            }
        }

        let colsSignature = "";
        this.allColumns.forEach(col => {
            colsSignature += col.name + "|";
        });

        const hash = MD5(colsSignature).toString();
        if (isView) {
            return "VIEW_" + `${activityClass}-${hash}`;
        }
        return `${activityClass}-${hash}`;
    }

    saveConfig() {
        // console.log('CONFIG:', this.config)
        SessionManager.configManager?.save("grid." + this.configId, this.config, SCOPE_DEVICE_SIZE);
    }

    loadConfig() {

        if (this.configId === this.configLoaded) {
            return;
        }
        this.configId = this.detectConfigId();
        this.configLoaded = this.configId;

        let conf = SessionManager.configManager?.load("grid." + this.configId);
        if (conf) {
            this.config = conf;
            this.dataGrid.colsVersion += 1;
        }
        if (this.config && this.config.pinnedCols == null) {
            this.config.pinnedCols = 0;
        }
    }

    /**

     that.gridHeader.dimensionCol.state.indexes
     Array(5) [0, 1, 2, 3, 4]


     that.gridHeader.dimensionCol.state.sizes
     Object {0: 110, 1: 149, 2: 110, 3: 150, 4: 110}

     */

    onBaseGridHeaderResize(col){
        this.config.cols[col.colId].size = col.size;
        this.saveConfig();
    }

    onBaseGridOrderChange(col) {
        //this.saveConfig();
    }

    onHeaderResize(e) {
        try {
            //let gridHeaderCols = this.dataGrid.gridHeaderCols;
            let changedCols = e.detail;
            // console.log(JSON.stringify(changedCols));
            for (let [i, newSize] of Object.entries(changedCols)) {
                if (i < 0) {
                    return;
                }
                let realColIdx = this.visibleColumns2AllCoumnsMap[i];
//TODO return
                let realCol = this.allColumns[realColIdx];
                this.config.cols[realCol.colId].size = newSize;
            }

            this.saveConfig();
        }catch (e){
            console.error(e);
        }
    }

    /**
     * @param {Object.<string, boolean>} visibilityData
     * @param {Object.<string, ColumnConfig>} colsConfig
     */
    setColumnsVisibility(visibilityData, orderData, pinnedCols = 0) {
        /** @type {Object.<string, ColumnConfig>} */
        if(!SessionManager.hasFlag(FeatureFlag.GRID_V3)){
            pinnedCols = 0;
        }

        let colsConfig = this.config.cols;
        let allVisible = 0;
        let firstCol = null;
        for (const [colId, visible] of Object.entries(visibilityData)) {
            if (firstCol === null) {
                firstCol = colId;
            }
            if (!colsConfig.hasOwnProperty(colId)) {
                colsConfig[colId] = new ColumnConfig();
            }
            colsConfig[colId].visible = visible;
            colsConfig[colId].order = (orderData && orderData.hasOwnProperty(colId)) ? orderData[colId] : -10;

            if (visible) {
                allVisible++;
            }
        }
        if (!allVisible) {
            colsConfig[firstCol].visible = true;
        }
        if(allVisible <= pinnedCols){
            pinnedCols = allVisible -1;
            if(pinnedCols < 0){
                pinnedCols = 0;
            }
        }
        this.config.pinnedCols = pinnedCols;
        this.dataGrid.colsVersion += 1;
        this.dataGrid.refresh();

        this.saveConfig();
    }


    resetConfig() {
        this.config = {cols: {}, pinnedCols: 0};
        this.saveConfig();
        this.dataGrid.colsVersion += 1;
        this.dataGrid.refresh();
    }

    resetConfigForDynamicColumns() {
        if(this.dataGrid?.listOfDynamicPreparedObjectForColumns !== null && this.dataGrid?.listOfDynamicPreparedObjectForColumns.length !== 0) {
            /** @type {ColumnDefinition[]} */
            let rawColumns = this.dataGrid.columns;
            for (const key in this.config.cols) {
                const found = rawColumns.some(column => column.colId === key || column.id === key);
                if (!found) {
                    delete this.config.cols[key];
                }
            }
            this.saveConfig();
            this.dataGrid.colsVersion += 1;
            this.dataGrid.filteredItems?.refresh();
        }
    }

    /**
     * @return {ColVisibility[]}
     */
    getColumnsVisibility() {
        /** @type {Object.<string, ColumnConfig>} */
        let columnsConfig = this.config.cols;

        let res = Array();
        this.allColumns.forEach((col) => {
            let comboData = new ColVisibility();
            comboData.colId = col.colId;
            comboData.visible = col.visible !== null && col.visible !== undefined ? col.visible : true;
            comboData.formConfig = col.formConfig;
            comboData.isDynamicCell = col.isDynamicCell;
            if (columnsConfig.hasOwnProperty(comboData.colId)) {
                let colProps = columnsConfig[comboData.colId];
                if (colProps.visible !== null) {
                    comboData.visible = colProps.visible;
                }
            }
            comboData.colLabel = col.name;
            res.push(comboData)

        });
        res.sort((a, b) => {
            let aOrder = (columnsConfig.hasOwnProperty(a.colId) ? columnsConfig[a.colId].order : null) ?? 100000000;
            let bOrder = (columnsConfig.hasOwnProperty(b.colId) ? columnsConfig[b.colId].order : null) ?? 100000000;

            if (a.isDynamicCell || a.formConfig?.dynamicColumns?.length) {
                if (aOrder === -10) {
                    aOrder = 999999999;
                }
            }
            if (b.isDynamicCell || b.formConfig?.dynamicColumns?.length) {
                if (bOrder === -10) {
                    bOrder = 999999999;
                }
            }
            return aOrder - bOrder;
        });

        let isView = false;
        if (this.dataGrid?.$props?.form?.isView) {
            isView = true;
        }
        if (!isView) {
            return res;
        }
        return this.sortColumnsForView(res, "colId");
    }

    /**
     * @returns {ColumnDefinition[]}
     */
    processColumns(colsVersion, form) {

        let NO_COMPARATOR_CLASS = "com.silkplm.engine.forms.schema.definition.defaults.NoComparator";
        let ascCol = this.dataGrid?.$el?.querySelector?.('div.asc');
        let descCol = this.dataGrid?.$el?.querySelector?.('div.desc');

        // console.log('ascCol: ', ascCol);
        // console.log('descCol: ', descCol);

        let orderedColumns = this.orderedColumns.filter(col => col.name != ''); // when the multi-select mode is enabled it is necessary to filter the first column from the this.orderedColumn

        let sorteredColumn;
        let sorteredColumDataColValue = '';
        let sortDir = '';
        if (ascCol || descCol) {
            sorteredColumDataColValue = ascCol?.getAttribute('data-col') || descCol?.getAttribute('data-col');
            sorteredColumn = orderedColumns[sorteredColumDataColValue];
            sortDir = ascCol ? 'asc' : 'desc';
            // console.log("dg2.sort" + sortDir);
        } else {
            // console.log("dg2.sort - no sort for col");
        }

        this.loadConfig();

        /** @type {ColumnDefinition[]} */
        let rawColumns = this.dataGrid.columns;
        /** @type {Object.<string, ColumnConfig>} */
        let colsConfig = this.config.cols;

        let cols = [];
        let allCols = [];
        this.visibleColumns2AllCoumnsMap = [];
        if (this.dataGrid.metaStatus || this.multiSelectEnabled) {
            let key = "__meta__";
            let templateM;
            let v3Template;

            if(!this.v3) {
                if (this.renderers.hasOwnProperty(key)) {
                    templateM = this.renderers[key];
                } else {
                    let grid = this.dataGrid;
                    let cls = {
                        ...MetaRenderer, beforeMount: function () {
                            this.dataGrid = grid;
                        }
                    };

                    templateM = VGridVueTemplate(Vue.extend(cls));
                    this.renderers[key] = templateM;
                }
                templateM.funId = key;
            } else {
                if (V3Renderers.hasOwnProperty("__meta__")) {
                    v3Template = V3Renderers["__meta__"];
                } else {
                    v3Template = Vue.extend(MetaRenderer);
                    V3Renderers["__meta__"] = v3Template;
                }
            }

            cols.push(this.metaColumn = {
                name: "",
                prop: "__meta__",
                order: -100,
                autoSize: false,
                sortable: false,
                searchable: false,
                resizable: false,
                resize: false,
                size: 30,
                visible: true,
                'cellTemplate': templateM,
                'cellRendererClass': v3Template,
                toStr: null,
                toStrFromInstance: null,
                toFilterStr: null,
                editorComponent: null,
                pin: 'colPinStart',
                multiSelectEnabled: this.multiSelectEnabled,
                grid: this.dataGrid,

                colId: "__meta__",
                id: "__meta__"
            });
        } else {

            // cols.push({
            //     name: "",
            //     prop: "__meta__",
            //     order: -100,
            //     autoSize: false,
            //     sortable: false,
            //     searchable: false,
            //     resizable: false,
            //     resize: false,
            //     size: 5,
            //     visible: true,
            //     toStr: ()=>"",
            //     toStrFromInstance: null,
            //     toFilterStr: null,
            //     editorComponent: null,
            //     pin: 'colPinStart',
            //     multiSelectEnabled: false,
            //     grid: this.dataGrid,
            //     colId: "__meta__",
            //     id: "__meta__"
            // });
        }
        //console.log("Processing cols");
        rawColumns.forEach((c, i) => {
            let clone = _.clone(c);
            if (clone.fieldConfig?.securedFieldConfig ?? false) {
                let securedFieldConfig = clone.fieldConfig.securedFieldConfig;
                let effectivePermissionRead = securedFieldConfig.effectivePermissionRead;
                if (securedFieldConfig.restrictedRead && !SessionManager.hasPermission(effectivePermissionRead)) {
                    return;
                }
            }
            let visible = c.visible ?? true;
            let colId = i + ":" + (c.prop ?? c.name ?? "?");

            if (c.id) {
                colId = c.id;
            } else {
                clone.id = colId;
            }

            clone.order = -10;

            clone.colId = colId;

            if (colsConfig.hasOwnProperty(colId)) {
                let colProps = colsConfig[colId];
                clone.order = colsConfig[colId].order ?? -10;

                if (colProps.visible != null) {
                    visible = colProps.visible;
                }

                // clone.order = colProps.order ?? 1000;

                if (colProps.size !== null) {
                    clone.size = colProps.size;
                    clone.autoSize = false;
                } else {
                    if (clone.size === undefined || clone.size === null) {
                        clone.size = 75;
                    }
                }
            } else {
                colsConfig[colId] = new ColumnConfig();
            }
            clone.visible = visible;
            clone.autoSize = clone.autoSize ?? true;
            clone.prop = clone.prop ?? "";
            if (clone.searchable === undefined) {
                clone.searchable = true;
            }

            if (clone?.fieldConfig?.comparatorFunction) {
                let mod = window['__rt_module'];
                clone.comparator = mod[clone?.fieldConfig?.comparatorFunction];
            }
            if(!this.v3) {
                clone.cellProperties = ({prop, model, data, column}) => {
                    if (column?.fieldConfig?.styleFunction) {
                        let style = RtModule()[column.fieldConfig.styleFunction].call(model[prop], model, column.formConfig, null);
                        if (!style) {
                            return {
                                style: {
                                    userSelect: 'none',
                                },
                            };
                        }
                        style.userSelect = 'none';
                        return {style};
                    }
                    return {
                        style: {
                            // color: 'red',
                            userSelect: 'none',
                            // zIndex: 100
                        },
                        // class: {
                        //     'bank': true,
                        //     'my-class': true
                        // }
                    };
                }
            } else {
                if(clone?.fieldConfig?.styleFunction){
                    clone.cellProperties = function({prop, model, data, column}) {
                        try {
                            let style = '';
                            if(column.fieldConfig.computed && model && model.__computed__){
                                style = RtModule()[column.fieldConfig.styleFunction].call(model.__computed__[prop], model.__computed__, column.formConfig, null);
                            }else if(!column.fieldConfig.computed && model && model.__computed__) {
                                style = RtModule()[column.fieldConfig.styleFunction].call(model[prop], model, column.formConfig, null);
                            }
                            return {style};
                        } catch (e) {
                            console.error(e);
                            return {
                                style: ''
                            }
                        }
                    }
                }
            }
            allCols.push(clone);

            if (!visible) {
                return;
            }

            // clone.comparator = (a,b) => {
            //
            //
            //
            //     if(a === b) {
            //         return 0;
            //     }
            //     return a > b ? 1 : -1;
            // }

            if(SessionManager.hasFlag(FeatureFlag.STREAMING_LISTS)){
                if (!clone.hasOwnProperty("template")) {
                    clone.template = DefaultCellTextRenderer;

                    if(V3Renderers["__defaultText"]){
                        clone.cellRendererClass =V3Renderers["__defaultText"];
                    } else {
                        V3Renderers["__defaultText"] = Vue.extend(DefaultCellTextRenderer);
                        clone.cellRendererClass = V3Renderers["__defaultText"];
                    }
                }
            }

            if (clone.hasOwnProperty("template")) {
                let key = colId;

                if(!this.dataGrid.v3) {
                    if (this.renderers.hasOwnProperty(key)) {
                        clone.cellTemplate = this.renderers[key];
                    } else {
                        if (typeof clone.template === "string" || clone.template instanceof String) {
                            let component = {
                                props: ["rowIndex", "prop", "model", "column", "data"],
                                template: clone.template
                            }
                            if (SessionManager.hasFlag(FeatureFlag.STREAMING_LISTS)) {
                                const dynamicClass = {
                                    ...LoadingCellRenderer,
                                    methods: {
                                        ...LoadingCellRenderer.methods,
                                        rendererFactoryClass: () => Vue.extend(component)
                                    }
                                };
                                clone.cellTemplate = VGridVueTemplate(Vue.extend(dynamicClass));
                            } else {
                                clone.cellTemplate = VGridVueTemplate(Vue.extend(component));
                            }
                        } else {
                            if (SessionManager.hasFlag(FeatureFlag.STREAMING_LISTS)) {

                                const dynamicClass = {
                                    ...LoadingCellRenderer,
                                    methods: {
                                        ...LoadingCellRenderer.methods,
                                        rendererFactoryClass: () => Vue.extend(clone.template)
                                    }
                                };
                                clone.cellTemplate = VGridVueTemplate(Vue.extend(dynamicClass));
                            } else {
                                clone.cellTemplate = VGridVueTemplate(clone.template);
                            }
                        }
                        clone.cellTemplate.funId = key;

                        this.renderers[key] = clone.cellTemplate;
                    }
                } else {

                    // v3
                    let rendererKey = clone.template.name;
                    if (V3Renderers.hasOwnProperty(rendererKey)) {
                        clone.cellRendererClass = V3Renderers[rendererKey];
                    } else {
                        if (typeof clone.template === "string" || clone.template instanceof String) {
                            // console.log("!!!! string col");
                            let component = {
                                props: ["rowIndex", "prop", "model", "column", "data"],
                                template: clone.template
                            }
                            if (SessionManager.hasFlag(FeatureFlag.STREAMING_LISTS)) {
                                const dynamicClass = {
                                    ...LoadingCellRenderer,
                                    methods: {
                                        ...LoadingCellRenderer.methods,
                                        rendererFactoryClass: function(){return Vue.extend(component); }
                                    }
                                };
                                clone.cellRendererClass = Vue.extend(dynamicClass);
                            } else {
                                clone.cellRendererClass = Vue.extend(component);
                            }
                        } else {
                            if (SessionManager.hasFlag(FeatureFlag.STREAMING_LISTS)) {

                                const dynamicClass = {
                                    ...LoadingCellRenderer,
                                    methods: {
                                        ...LoadingCellRenderer.methods,
                                        rendererFactoryClass: makeRendererFactory(clone.template)
                                    }
                                };
                                clone.cellRendererClass = Vue.extend(dynamicClass);
                                V3Renderers[rendererKey] = clone.cellRendererClass;

                            } else {
                                clone.cellRendererClass = clone.template;
                            }
                        }
                        //clone.cellRendererClass.funId = key;

                        this.v3renderers[key] = clone.cellRendererClass;
                    }
                }
                // v3

            }
            this.visibleColumns2AllCoumnsMap.push(i)
            cols.push(clone);
        });
        cols.forEach(col => {
            if (col.isDynamicCell || col.formConfig?.dynamicColumns?.length) {
                if (!col.order || col.order === -10) {
                    col.order = 999999999;
                }
            }
        });
        allCols.forEach(col => {
            if (col.isDynamicCell || col.formConfig?.dynamicColumns?.length) {
                if (!col.order || col.order === -10) {
                    col.order = 999999999;
                }
            }
        });
        cols.sort((a, b) => {
            let orderA = a.order ?? -1;
            let orderB = b.order ?? -1;
            return orderA - orderB;
        });
        allCols.sort((a, b) => {
            let orderA = a.order ?? -1;
            let orderB = b.order ?? -1;
            return orderA - orderB;
        });
        cols.forEach(col => {
            delete col.order;
            col.dataGrid = this.dataGrid;
            col.colsManager = this;
        });


        // EventBus.$on(Events.keepDefaultSorting, () => {
        //     console.log('keepDefaultSorting');
        //     this.applyDefaultSorting = false;
        // });

        let colNumber = 0;
        let eidColumns = [];
        let sortedEnabledByAnyColumn = false;

        allCols.forEach(col => {
            colNumber++;

            if (!sorteredColumn) {
                if (col.fieldConfig?.typeOfSortOfColumn) {
                    if (col.fieldConfig.fieldName.includes("EID")){
                        eidColumns.push(col);
                    } else {
                        sortedEnabledByAnyColumn = true;
                        col.order = col.fieldConfig.typeOfSortOfColumn;
                    }
                }
            }
        })
        if (!sortedEnabledByAnyColumn && (form?.resultComparator === NO_COMPARATOR_CLASS)){
            eidColumns.forEach(col => {
                col.order = col.fieldConfig.typeOfSortOfColumn;
            });
        }

        // let pinedCols = ['EID', 'HSCode', 'numberOfProduct'];

        // cols.forEach(col => {
        //
        //     let isPinnedCol = pinedCols.filter(x => x == col.colId).length > 0
        //
        //     if(isPinnedCol) {
        //         col.pin = 'colPinStart';
        //     } else {
        //         //increase data-col value by pinnedCols length + 1;
        //         // col.cellTemplate.smoeattr = 'yo';
        //     }
        //
        // });


        this.allColumns = allCols;
        this.orderedColumns = cols;
        if (sorteredColumn) {
            this.orderedColumns.forEach(column => {
                if (column.prop === sorteredColumn.prop) {
                    column.order = sortDir;
                }
            });
        }


        let isView = false;
        if (this.dataGrid?.$props?.form?.isView) {
            isView = true;
        }
        if (!isView) {
            return cols;
        }
        return this.sortColumnsForView(cols, "prop");
    }

    sortColumnsForView(cols, propName) {
        // SessionManager.configManager?.save("grid." + this.configId, this.config, SCOPE_DEVICE_SIZE);
        let conf = SessionManager.configManager?.load("grid." + this.configId);
        if (!conf) {
            return cols;
        }
        let entryValue = conf.cols;
        if (!entryValue) {
            return cols;
        }
        let keysSize = Object.keys(conf.cols).length;
        if (keysSize === 0) {
            return cols;
        }
        let newCols = [];

        let oldCols = [];
        for (let col of cols) {
            if (col.colId === "__meta__") {
                oldCols.push(col);
                continue;
            }
            if (!entryValue[col[propName]]) {
                newCols.push(col);
            } else {
                oldCols.push(col);
            }
        }
        newCols.sort((a, b) => b.gridOrder - a.gridOrder);
        return oldCols.concat(newCols);
    }
}